// Демо-данные для показа шаблона без Supabase и без сети.
// Включается переменной VITE_DEMO=1. Форма данных совпадает с тем,
// что отдают запросы в lib/catalog.ts, lib/booking.ts, lib/owner.ts.
//
// Записи храним в localStorage, чтобы владелец видел запись, сделанную
// клиентом в другой вкладке (в памяти вкладки данные не разделяются).

export const DEMO_TENANT = {
  id: '00000000-0000-0000-0000-000000000001',
  slug: 'demo',
  name: 'Салон красоты',
  city: 'Москва',
  timezone: 'Europe/Moscow',
  phone: '+7 999 123-45-67',
  address: 'ул. Примерная, 12',
  theme: { accent: '#A56F52', background: '#F6F1EB', text: '#24201D', mood: 'тёплый минимализм' },
  business: {
    tagline: 'Маникюр и педикюр без ожидания',
    about:
      'Небольшая студия в центре. Два мастера, три услуги, запись онлайн без звонков.',
    highlights: [
      { title: 'Свободное окно', text: 'Запись за 30 секунд, без звонков и переписки.' },
      { title: 'Без двойных записей', text: 'Слот держится на уровне базы, а не на честном слове.' },
      { title: 'Удобный перенос', text: 'Клиент сам отменяет или переносит визит.' }
    ],
    addressHint: 'Вход со двора, второй этаж',
    mapUrl: 'https://yandex.ru/maps/',
    social: { instagram: null, telegram: 'https://t.me/demo', whatsapp: '+79991234567', vk: null }
  },
  demo: true
};

const RESOURCE_ID = '00000000-0000-0000-0000-000000000101';

type DemoBooking = {
  id: string;
  tenant_id: string;
  service_id: string;
  staff_id: string;
  resource_id: string;
  starts_at: string;
  ends_at: string;
  client_name: string;
  client_phone: string;
  price: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'done';
};

// Записи, созданные в демо-сессии. localStorage, чтобы их видел
// и кабинет владельца, открытый в другой вкладке того же браузера.
import { DEMO_BOOKINGS_KEY, pingDemoBookings } from './demoEvents';
import { catalogFromSettings } from './salonCatalog';
import { DEFAULT_AVAILABLE_TIMES, readSalonSettings, WEEK_DAYS_ORDER } from './salonSettings';
import { readDemoScheduleBlocks } from './scheduleBlocks';

const STORAGE_KEY = DEMO_BOOKINGS_KEY;

function readCreated(): DemoBooking[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as DemoBooking[]) : [];
  } catch {
    return [];
  }
}

function writeCreated(list: DemoBooking[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* приватный режим — запись живёт только на текущей странице */
  }
}

export function demoServices() {
  return catalogFromSettings().services;
}

// Мастера, которые делают выбранную услугу.
export function demoStaff(serviceId: string) {
  return catalogFromSettings()
    .staff.filter((s) => s.services.includes(serviceId))
    .map((s) => ({ id: s.id, name: s.name, role: s.role }));
}

export function demoSlots(serviceId: string, staffId?: string) {
  const { services, staff } = catalogFromSettings();
  const svc = services.find((s) => s.id === serviceId);
  if (!svc) return [];
  const out = [];
  const created = readCreated();
  const blocks = readDemoScheduleBlocks();
  const settings = readSalonSettings();
  const availableTimes = settings.availableTimes ?? DEFAULT_AVAILABLE_TIMES;
  const now = new Date();
  const base = new Date(now);
  base.setHours(0, 0, 0, 0);

  const masters = staffId
    ? staff.filter((s) => s.id === staffId)
    : staff.filter((s) => s.services.includes(serviceId));

  for (let d = 0; d < 7; d += 1) {
    const day = new Date(base);
    day.setDate(base.getDate() + d);
    const dayKey = WEEK_DAYS_ORDER[(day.getDay() + 6) % 7];
    for (const time of availableTimes[dayKey] ?? []) {
      const [hour, minute] = time.split(':').map(Number);
      const start = new Date(base);
      start.setDate(base.getDate() + d);
      start.setHours(hour, minute, 0, 0);
      if (start <= now) continue;
      const end = new Date(start.getTime() + svc.duration_min * 60_000);
      for (const st of masters) {
        if (!st.services.includes(serviceId)) continue;
        const blocked = blocks.some((b) =>
          (b.staff_id === null || b.staff_id === st.id)
          && new Date(b.starts_at) < end && new Date(b.ends_at) > start
        );
        if (blocked) continue;
        const busy = created.some(
          (b) => (b.status ?? 'confirmed') !== 'cancelled' && b.staff_id === st.id
            && new Date(b.starts_at) < end && new Date(b.ends_at) > start
        );
        if (busy) continue;
        out.push({
          startsAt: start.toISOString(),
          endsAt: end.toISOString(),
          staffId: st.id,
          staffName: st.name,
          resourceId: RESOURCE_ID
        });
      }
    }
  }

  out.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  return out.slice(0, 60);
}

export function demoCreateBooking(input: {
  tenantId: string;
  serviceId: string;
  staffId: string;
  resourceId: string;
  startsAt: string;
  endsAt: string;
  clientName: string;
  clientPhone: string;
}) {
  const svc = catalogFromSettings().services.find((s) => s.id === input.serviceId);
  const created = readCreated();

  // Атомарность в демо: слот не должен пересекаться с уже существующей
  // записью у того же мастера. Это клиентский аналог EXCLUDE-констрейнта
  // в Postgres — без него два быстрых тапа создали бы две записи.
  const start = new Date(input.startsAt).getTime();
  const end = new Date(input.endsAt).getTime();
  const clash = created.some(
    (b) =>
      b.staff_id === input.staffId &&
      new Date(b.starts_at).getTime() < end &&
      new Date(b.ends_at).getTime() > start
  );
  if (clash) {
    throw new Error('Это время уже занято. Выберите другое окно.');
  }

  const id = `demo-${Date.now()}-${created.length + 1}`;
  created.push({
    id,
    tenant_id: input.tenantId,
    service_id: input.serviceId,
    staff_id: input.staffId,
    resource_id: input.resourceId,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    client_name: input.clientName,
    client_phone: input.clientPhone,
    price: svc?.price ?? 0,
    status: 'confirmed'
  });
  writeCreated(created);
  // Сообщаем вкладке владельца: появилась новая запись.
  pingDemoBookings();
  return id;
}

// Записи для экрана «Мои записи» клиента в демо.
// В демо клиент один (текущая вкладка), поэтому это те же записи, что у владельца.
// В проде mybookings.ts фильтрует по телефону клиента, а не отдаёт всё.
export function demoMyBookings() {
  return demoOwnerBookings();
}

export function demoOwnerBookings() {
  const { services, staff } = catalogFromSettings();
  return readCreated()
    .map((b) => ({
      id: b.id,
      service_id: b.service_id,
      staff_id: b.staff_id,
      resource_id: b.resource_id,
      starts_at: b.starts_at,
      ends_at: b.ends_at,
      client_name: b.client_name,
      client_phone: b.client_phone,
      service_name: services.find((s) => s.id === b.service_id)?.name ?? 'Услуга',
      staff_name: staff.find((s) => s.id === b.staff_id)?.name ?? null,
      price: b.price,
      status: b.status ?? 'confirmed'
    }))
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
}

export function demoUpdateBookingStatus(id: string, status: DemoBooking['status']): void {
  const rows = readCreated();
  const booking = rows.find((row) => row.id === id);
  if (!booking) throw new Error('Запись не найдена');
  booking.status = status;
  writeCreated(rows);
  pingDemoBookings();
}

export function demoRescheduleBooking(
  id: string,
  slot: { staffId: string; resourceId: string; startsAt: string; endsAt: string }
): void {
  const rows = readCreated();
  const booking = rows.find((row) => row.id === id);
  if (!booking || booking.status === 'cancelled' || booking.status === 'done') {
    throw new Error('Эту запись уже нельзя перенести');
  }
  const start = Date.parse(slot.startsAt);
  const end = Date.parse(slot.endsAt);
  const clash = rows.some((row) => row.id !== id && row.status !== 'cancelled'
    && (row.staff_id === slot.staffId || row.resource_id === slot.resourceId)
    && Date.parse(row.starts_at) < end && Date.parse(row.ends_at) > start);
  if (clash) throw new Error('Это время уже занято. Выберите другое окно.');
  Object.assign(booking, {
    staff_id: slot.staffId,
    resource_id: slot.resourceId,
    starts_at: slot.startsAt,
    ends_at: slot.endsAt,
    status: 'confirmed'
  });
  writeCreated(rows);
  pingDemoBookings();
}
